import React, { useState, useEffect } from 'react';
import { Button } from 'common/components';
import { syncLivekitTrunks } from 'services/api/PhoneNumberAPI';
import { useGetAccessManager } from 'services/hooks/queries';
import { Tenant } from 'types/accessManager.type';
import {
    BuyNumbersTab,
    ReplaceSpamTab,
    CreateWorkflowTrunkTab,
    TestCarrierTab,
    DeleteTwilioAccountTab,
    OrphanNumbersTab,
} from './components';
import styles from './PhoneNumberManager.module.scss';

interface NotificationState {
    isVisible: boolean;
    message: string;
    type: 'success' | 'error';
}

type ActiveTab = 'buy' | 'spam' | 'createTrunk' | 'test' | 'orphans' | 'deleteAccount';

const PhoneNumberManager: React.FC = () => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('buy');
    const [isSyncingLivekit, setIsSyncingLivekit] = useState(false);
    const [notification, setNotification] = useState<NotificationState>({
        isVisible: false,
        message: '',
        type: 'success',
    });
    const [tenants, setTenants] = useState<Tenant[]>([]);

    const { accessManagerData, isAccessManagerLoading } = useGetAccessManager();

    // Load tenants from accessManagerData
    useEffect(() => {
        if (accessManagerData?.tenants) {
            setTenants(accessManagerData.tenants);
        }
    }, [accessManagerData]);

    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ isVisible: true, message, type });
        setTimeout(() => {
            setNotification({ isVisible: false, message: '', type: 'success' });
        }, 5000);
    };

    const handleSyncLivekitToTwilio = async () => {
        setIsSyncingLivekit(true);

        try {
            const syncResult = await syncLivekitTrunks();
            const warningsCount = syncResult.warnings?.length ?? 0;

            showNotification(
                `${syncResult.message} Synced ${syncResult.syncedTrunks}/${syncResult.totalTrunks} trunks across ${syncResult.processedTenants}/${syncResult.totalTenants} tenants` +
                    ` (${syncResult.failedTenants} failed tenants, ${syncResult.failedTrunks} failed trunks${warningsCount ? `, ${warningsCount} warnings` : ''}).`,
                syncResult.failedTenants > 0 || syncResult.failedTrunks > 0 ? 'error' : 'success'
            );
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to sync LiveKit trunks to Twilio.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSyncingLivekit(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Phone Number Manager</h1>
                <p className={styles.subtitle}>Manage phone numbers for tenants</p>
            </div>

            {notification.isVisible && (
                <div className={`${styles.notification} ${styles[notification.type]}`}>{notification.message}</div>
            )}

            <div className={styles.contentWrapper}>
                <div className={styles.verticalNav}>
                    <button
                        className={`${styles.navItem} ${activeTab === 'buy' ? styles.active : ''}`}
                        onClick={() => setActiveTab('buy')}
                    >
                        Buy Numbers
                    </button>
                    <button
                        className={`${styles.navItem} ${activeTab === 'spam' ? styles.active : ''}`}
                        onClick={() => setActiveTab('spam')}
                    >
                        Spam Verdicts
                    </button>
                    <button
                        className={`${styles.navItem} ${activeTab === 'createTrunk' ? styles.active : ''}`}
                        onClick={() => setActiveTab('createTrunk')}
                    >
                        Create Workflow Trunk
                    </button>
                    <button
                        className={`${styles.navItem} ${activeTab === 'test' ? styles.active : ''}`}
                        onClick={() => setActiveTab('test')}
                    >
                        Test Carrier
                    </button>
                    <button
                        className={`${styles.navItem} ${activeTab === 'orphans' ? styles.active : ''}`}
                        onClick={() => setActiveTab('orphans')}
                    >
                        Orphan Numbers
                    </button>
                    <button
                        className={`${styles.navItem} ${activeTab === 'deleteAccount' ? styles.active : ''}`}
                        onClick={() => setActiveTab('deleteAccount')}
                    >
                        Delete Twilio Account
                    </button>
                </div>

                <div className={styles.contentArea}>
                    {activeTab === 'buy' && (
                        <>
                            <div className={styles.buyTabActions}>
                                <Button
                                    type='button'
                                    variant='secondary'
                                    onClick={handleSyncLivekitToTwilio}
                                    disabled={isSyncingLivekit}
                                >
                                    {isSyncingLivekit ? 'Syncing...' : '🔄 Sync LiveKit -> Twilio'}
                                </Button>
                            </div>
                            <BuyNumbersTab
                                tenants={tenants}
                                isAccessManagerLoading={isAccessManagerLoading}
                                showNotification={showNotification}
                            />
                        </>
                    )}

                    {activeTab === 'spam' && (
                        <ReplaceSpamTab
                            tenants={tenants}
                            isAccessManagerLoading={isAccessManagerLoading}
                            showNotification={showNotification}
                        />
                    )}

                    {activeTab === 'createTrunk' && (
                        <CreateWorkflowTrunkTab
                            tenants={tenants}
                            isAccessManagerLoading={isAccessManagerLoading}
                            showNotification={showNotification}
                        />
                    )}

                    {activeTab === 'test' && (
                        <TestCarrierTab
                            tenants={tenants}
                            isAccessManagerLoading={isAccessManagerLoading}
                            showNotification={showNotification}
                        />
                    )}

                    {activeTab === 'orphans' && (
                        <OrphanNumbersTab tenants={tenants} isAccessManagerLoading={isAccessManagerLoading} />
                    )}

                    {activeTab === 'deleteAccount' && (
                        <DeleteTwilioAccountTab
                            tenants={tenants}
                            isAccessManagerLoading={isAccessManagerLoading}
                            showNotification={showNotification}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PhoneNumberManager;
